import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  collection,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import { updateProfile, deleteUser } from 'firebase/auth'
import { db, auth } from '@/services/firebase/config'
import type { UserProfile } from '@/types/user'

export type { UserProfile }

const USERS_COLLECTION = 'users'

// Get user profile by ID
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const docRef = doc(db, USERS_COLLECTION, uid)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { uid: docSnap.id, ...docSnap.data() } as UserProfile
}

// Create user profile (called on signup)
export async function createUserProfile(
  uid: string,
  email: string,
  displayName: string
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, uid)
  await setDoc(docRef, {
    email,
    displayName,
    verified: false,
    business: false,
    createdAt: serverTimestamp(),
  })
}

// Update user profile
export async function updateUserProfile(
  uid: string,
  data: Partial<Omit<UserProfile, 'uid' | 'email' | 'createdAt'>>
): Promise<void> {
  const docRef = doc(db, USERS_COLLECTION, uid)

  // Strip undefined values — Firestore updateDoc throws on undefined
  const cleanData: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      cleanData[key] = value
    }
  }

  await updateDoc(docRef, cleanData)

  // Also update Firebase Auth profile if displayName changed
  if (data.displayName && auth.currentUser) {
    await updateProfile(auth.currentUser, {
      displayName: data.displayName,
    })
  }
}

// Check if user profile exists
export async function userProfileExists(uid: string): Promise<boolean> {
  const docRef = doc(db, USERS_COLLECTION, uid)
  const docSnap = await getDoc(docRef)
  return docSnap.exists()
}

// Get user profile by store slug
export async function getUserBySlug(slug: string): Promise<UserProfile | null> {
  const q = query(
    collection(db, USERS_COLLECTION),
    where('storeSlug', '==', slug),
    limit(1)
  )
  const snap = await getDocs(q)
  if (snap.empty) return null
  const docSnap = snap.docs[0]
  return { uid: docSnap.id, ...docSnap.data() } as UserProfile
}

// Check if a store slug is available
export async function isSlugAvailable(slug: string, excludeUserId?: string): Promise<boolean> {
  const q = query(
    collection(db, USERS_COLLECTION),
    where('storeSlug', '==', slug),
    limit(1)
  )
  const snap = await getDocs(q)
  if (snap.empty) return true
  // If the only match is the excluded user, it's available (they already own it)
  if (excludeUserId && snap.docs.length === 1 && snap.docs[0].id === excludeUserId) return true
  return false
}

// Export all user data (NZ Privacy Act 2020 compliance)
export async function exportUserData(uid: string) {
  const data: Record<string, unknown> = {}

  // User profile
  const profileDoc = await getDoc(doc(db, USERS_COLLECTION, uid))
  if (profileDoc.exists()) {
    data.profile = { id: profileDoc.id, ...profileDoc.data() }
  }

  // Saved items
  const savedSnap = await getDocs(collection(db, USERS_COLLECTION, uid, 'saved'))
  data.savedItems = savedSnap.docs.map(d => ({ id: d.id, ...d.data() }))

  // Listings
  const listingsSnap = await getDocs(
    query(collection(db, 'listings'), where('sellerId', '==', uid))
  )
  data.listings = listingsSnap.docs.map(d => ({ id: d.id, ...d.data() }))

  // Conversations
  const convSnap = await getDocs(
    query(collection(db, 'conversations'), where('participants', 'array-contains', uid))
  )
  data.conversations = convSnap.docs.map(d => ({ id: d.id, ...d.data() }))

  return data
}

// Delete user account and associated data (NZ Privacy Act 2020 compliance)
export async function deleteUserAccount(uid: string): Promise<void> {
  const errors: string[] = []

  // 1. Delete saved items subcollection
  try {
    const savedSnap = await getDocs(collection(db, USERS_COLLECTION, uid, 'saved'))
    for (const savedDoc of savedSnap.docs) {
      await deleteDoc(savedDoc.ref)
    }
  } catch (e) {
    errors.push('Failed to delete saved items')
  }

  // 2. Soft-delete listings (set status to 'deleted')
  try {
    const listingsSnap = await getDocs(
      query(collection(db, 'listings'), where('sellerId', '==', uid))
    )
    for (const listingDoc of listingsSnap.docs) {
      await updateDoc(listingDoc.ref, { status: 'deleted', updatedAt: serverTimestamp() })
    }
  } catch (e) {
    errors.push('Failed to delete listings')
  }

  // 3. Delete user profile document
  try {
    await deleteDoc(doc(db, USERS_COLLECTION, uid))
  } catch (e) {
    errors.push('Failed to delete profile')
  }

  // 4. Delete Firebase Auth account LAST (hardest to reverse)
  if (errors.length > 0) {
    throw new Error(
      `Account partially deleted. Failed steps: ${errors.join(', ')}. Please contact support.`
    )
  }

  try {
    if (auth.currentUser) {
      await deleteUser(auth.currentUser)
    }
  } catch (e) {
    throw new Error(
      'Failed to delete authentication account. Your data has been removed but your login still exists. Please contact support.'
    )
  }
}
